import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Check, Loader2 } from "lucide-react";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import { Button } from "@/components/ui/button";
import { StartFreeButton } from "@/components/marketing/cta";
import { replaySource, SyreWebinarSection } from "@/components/marketing/syre-webinar";
import { useSEO } from "@/hooks/use-seo";
import { useSyreOffer } from "@/hooks/use-syre-offer";
import { SYRE_SEO } from "@/data/marketing-seo";
import { track } from "@/lib/marketing/analytics";
import { redirectTo } from "@/lib/marketing/navigation";
import { TERMS_PRICING_HREF, signupHref } from "@/lib/marketing/links";
import { LIFETIME_REFUND_DAYS, TRIAL_DAYS, formatUsd } from "@/lib/marketing/pricing-copy";
import { countdownTo, formatCloseEt, parseUnits, parseEventId, lifetimeClaimPath, startLifetimeCheckout, unitOptions } from "@/lib/marketing/syre-offer";
import { stashReturnTo } from "@/lib/auth-return-to";
import { PROOF_TILES, PROOF_LINE } from "@/data/proof-tiles";
import { FeatureExplorer } from "@/components/feature-explorer";
import { SYRE_OCTOBER_EVENT } from "@shared/syre-event";
import { SyreLockBonus } from "@/components/marketing/syre-lock-bonus";

const CHECKOUT_ERRORS: Record<string, string> = {
  closed: "This SYRE offer has closed.",
  already_lifetime: "Your account already has the most lifetime properties allowed (20).",
  over_cap: "That would take your lifetime plan past 20 properties. Pick fewer properties.",
  unavailable: "The lifetime deal isn't available on this account. Please email travis@padpulse.io.",
  error: "We couldn't start checkout. Please try again, or email travis@padpulse.io.",
  sign_in: "Sign in to continue your selected lifetime purchase.",
};

/** The seven "What's included" rows, same wording as the printed banner. */
export const SYRE_INCLUDED_ROWS: Array<{ title: string; body: string }> = [
  { title: "Never lose a member message.", body: "PadSplit and member-portal messages in one team inbox. AI drafts replies, and your rules auto-send the routine ones." },
  { title: "Fill rooms with the right members.", body: "Every booking request in one queue, on or off PadSplit. Your screening form goes out automatically." },
  { title: "Locks and thermostats in one place.", body: "Every door code in one place, with battery and temperature alerts." },
  { title: "Maintenance that closes itself.", body: "Requests become tasks for your vendors, with photo proof. Supplies reorder on schedule." },
  { title: "Cleaner houses, less effort.", body: "Recurring cleaning schedules for every house, by member, with form submissions to tie it all together." },
  { title: "Know your numbers.", body: "A live P&L for every property, with imported PadSplit earnings and read-only transactions from supported U.S. bank accounts." },
  { title: "Find your next property.", body: "Up to 3 daily buy box agents find the houses that fit and send them to you." },
];

async function isSignedIn(): Promise<boolean> {
  try {
    const res = await fetch("/api/app/auth/me", { credentials: "include" });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * SYRE lifetime deal (F7 v2). Printed on the reusable banner's QR code as
 * https://padpulse.io/syre. noindex, not in the sitemap or navigation. Every
 * date comes from the offer-window API (or the pricing-module fallback).
 */
export default function SyrePage() {
  useSEO({ title: SYRE_SEO.title, description: SYRE_SEO.description, path: SYRE_SEO.path, noindex: true });
  const [, navigate] = useLocation();
  const { offer, now } = useSyreOffer();
  const [units, setUnits] = useState(() =>
    typeof window === "undefined" ? 2 : parseUnits(new URLSearchParams(window.location.search).get("units") ?? "2"),
  );
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const bonusImpressionTracked = useRef(false);
  const options = unitOptions(offer);
  const perUnit = offer.propertyAllowancePerUnit;
  const hasReplay = Boolean(offer.webinarReplayUrl && replaySource(offer.webinarReplayUrl));
  const eventOffer = offer;
  const hasLockPromotion = Boolean(eventOffer.promotionLabel && eventOffer.bonusClosesAt);
  const bonusLeft = eventOffer.bonusClosesAt ? countdownTo(eventOffer.bonusClosesAt, now) : null;

  useEffect(() => {
    if (hasLockPromotion && !bonusImpressionTracked.current) {
      bonusImpressionTracked.current = true;
      track("syre_bonus_impression", { event_id: eventOffer.eventId ?? null });
    }
  }, [hasLockPromotion, eventOffer.eventId]);

  async function handleClaim() {
    if (claiming) return;
    track("syre_claim_clicked", { units });
    setClaimError(null);
    setClaiming(true);
    const selectedEventId = parseEventId(new URLSearchParams(window.location.search).get("eventId")) ?? offer.eventId;
    if (!(await isSignedIn())) {
      setClaiming(false);
      navigate(signupHref({ offer: "syre-lifetime", units: String(units), ...(selectedEventId ? { eventId: String(selectedEventId) } : {}) }));
      return;
    }
    const result = await startLifetimeCheckout(units, selectedEventId);
    if (result.ok) {
      redirectTo(result.url);
      return;
    }
    setClaiming(false);
    if (result.reason === "sign_in") {
      stashReturnTo(lifetimeClaimPath(units, selectedEventId));
      navigate("/app/login");
      return;
    }
    setClaimError(
      result.reason === "over_cap" && result.maxUnits
        ? `You can add up to ${result.maxUnits * perUnit} more lifetime properties (20 total). Pick fewer properties.`
        : CHECKOUT_ERRORS[result.reason],
    );
  }

  function chooseTier(nextUnits: number) {
    setUnits(nextUnits);
    track("syre_tier_selected", { units: nextUnits, properties: nextUnits * perUnit });
  }

  function replayJump() {
    return hasReplay ? (
      <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
        <a href="#webinar" className="text-blue-100 underline underline-offset-4" data-testid="link-syre-webinar-jump">
          Watch the webinar
        </a>
        <a
          href={offer.webinarReplayUrl!}
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-white underline decoration-brand-accent underline-offset-4"
          data-testid="link-syre-webinar-external"
        >
          Open webinar link
        </a>
      </div>
    ) : null;
  }

  function eventResourceActions() {
    return (eventOffer.presentationUrl || eventOffer.qaUrl) ? (
      <div className="mt-5 flex flex-wrap justify-center gap-3">
        {eventOffer.presentationUrl && (
          <a href={eventOffer.presentationUrl} target="_blank" rel="noreferrer"
            onClick={() => track("syre_presentation_clicked", { event_id: eventOffer.eventId ?? null })}
            className="rounded-full border border-white/35 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent"
            data-testid="link-syre-presentation">
            Download presentation
          </a>
        )}
        {eventOffer.qaUrl && (
          <a href={eventOffer.qaUrl} target="_blank" rel="noreferrer"
            onClick={() => track("syre_qa_clicked", { event_id: eventOffer.eventId ?? null })}
            className="rounded-full border border-white/35 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent"
            data-testid="link-syre-qa">
            Join Q&amp;A
          </a>
        )}
      </div>
    ) : null;
  }

  const close = offer.closesAt ? formatCloseEt(offer.closesAt) : null;
  const closeLongDate = offer.closesAt
    ? new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", timeZone: "America/New_York" }).format(new Date(offer.closesAt))
    : null;
  const left = offer.closesAt ? countdownTo(offer.closesAt, now) : null;

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <PublicHeader />
      <div className="bg-gradient-to-br from-[#0a1628] via-[#0d2447] to-brand">
        <section className="px-4 pb-14 pt-28 text-center md:pb-20 md:pt-32" data-testid={offer.open ? "section-syre-open" : "section-syre-closed"}>
          <div className="mx-auto max-w-5xl">
            {offer.open ? (
              <>
                <p className="text-sm font-semibold uppercase tracking-wider text-brand-accent" data-testid="text-syre-eyebrow">
                  Scale Your Real Estate members only
                </p>
                <p className="mt-4 text-lg font-semibold text-white" data-testid="text-syre-tagline">Your co-living copilot.</p>
                <h1 className="mt-2 text-4xl font-bold tracking-tight text-white md:text-5xl" data-testid="text-syre-heading">
                  Pay for one year. Use PadPulse for life.
                </h1>
                <p className="mx-auto mt-4 max-w-2xl text-lg text-blue-100" data-testid="text-syre-sub">
                  {formatUsd(offer.priceCents)} once covers {perUnit} properties for life. Need more? Stack it up to {offer.maxUnits} times for{" "}
                  {offer.maxTotalAllowance} properties.
                </p>
                {replayJump()}
                {eventResourceActions()}

                <fieldset className="mx-auto mt-10 grid max-w-5xl grid-cols-1 gap-x-3 gap-y-8 min-[360px]:grid-cols-2 sm:gap-4 lg:grid-cols-4" data-testid="group-syre-units">
                  <legend className="mb-4 w-full text-center text-base font-semibold text-white">Select an option, then claim your lifetime deal.</legend>
                  {options.map((o) => {
                    const selected = o.units === units;
                    const highlightedTier = o.properties === 10 || o.properties === 20;
                    const lightSurface = selected;
                    return (
                      <label
                        key={o.units}
                        className={`flex min-h-[200px] cursor-pointer flex-col rounded-xl border-2 px-2.5 py-3 text-left transition focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-brand-accent sm:min-h-[216px] sm:px-3 ${o.properties === 20 ? `border-brand-accent ring-1 ring-brand-accent/20 shadow-lg shadow-brand-accent/10 ${selected ? "bg-white text-gray-900" : "bg-white/5 text-white"}` : o.properties === 10 ? `border-brand-accent/70 ${selected ? "bg-white text-gray-900" : "bg-white/5 text-white"}` : `${selected ? "border-white bg-white text-gray-900" : "border-white/30 bg-white/5 text-white hover:border-white/60"}`}`}
                        data-testid={`option-syre-units-${o.units}`}
                      >
                        <input type="radio" name="syre-units" value={o.units} checked={selected} onChange={() => chooseTier(o.units)} className="sr-only" />
                        <span className="block text-xl font-extrabold leading-tight">{o.properties} properties</span>
                        <span className={`mt-1 block text-sm font-medium ${lightSurface ? "text-gray-600" : "text-blue-100"}`}>{formatUsd(o.priceCents)}</span>
                        {eventOffer.standardLocks && <span className="mt-2 block text-xs">{o.units} lock{o.units === 1 ? "" : "s"} included, {formatUsd(o.units * 10000)} value</span>}
                        {highlightedTier && <span className={`mt-2 block text-xs font-bold ${lightSurface ? "text-brand" : "text-brand-accent"}`}>{o.properties === 20 ? "Best value" : "Most popular"}</span>}
                        {hasLockPromotion && (
                          <div className="mt-auto pt-3">
                            <SyreLockBonus lockCount={o.properties} remaining={bonusLeft} />
                          </div>
                        )}
                      </label>
                    );
                  })}
                </fieldset>

                {(eventOffer.standardLocks || hasLockPromotion) && (
                  <div className="mx-auto mt-5 max-w-5xl rounded-xl border border-white/20 bg-white/5 px-4 py-4 text-left text-sm text-blue-50 sm:px-5" data-testid="text-syre-lock-inclusion">
                    {hasLockPromotion ? (
                      <>
                        <strong className="text-white">{eventOffer.promotionLabel}</strong>
                        <span> — {units * perUnit} property locks included with this tier when payment is completed by {eventOffer.bonusClosesAt ? formatCloseEt(eventOffer.bonusClosesAt).date + " at " + formatCloseEt(eventOffer.bonusClosesAt).time + " ET" : "the offer cutoff"}.</span>
                        <p className="mt-1 text-blue-100">Signing up is not payment and does not reserve the bonus.</p>
                      </>
                    ) : (
                        <span>{units} lock{units === 1 ? "" : "s"} included, {formatUsd(units * 10000)} value.</span>
                    )}
                  </div>
                )}

                {close && left && (
                  <p className="mt-6 text-sm text-blue-100" data-testid="text-syre-countdown">
                    This SYRE offer closes {close.date} at {close.time} ET · {left.days}d {left.hours}h {left.minutes}m
                  </p>
                )}
                <div className="mt-6 flex flex-col items-center gap-3">
                  <p className="text-sm font-semibold text-white" aria-live="polite" data-testid="text-syre-selection">
                    Selected: {units * perUnit} properties · {formatUsd(units * offer.priceCents)} one-time
                  </p>
                  <Button
                    variant="ghost"
                    size="lg"
                    onClick={handleClaim}
                    disabled={claiming}
                    className="syre-gold-cta no-default-hover-elevate no-default-active-elevate relative isolate h-auto w-full max-w-sm overflow-hidden rounded-xl px-8 py-4 text-base font-bold hover:brightness-105 active:brightness-95 sm:w-auto"
                    data-testid="button-syre-claim"
                  >
                    {claiming && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                    Claim the lifetime deal
                  </Button>
                  <p className="text-sm font-semibold text-blue-100" data-testid="text-syre-guarantee">
                    {eventOffer.refundFloorAt
                      ? `Refundable through ${new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", month: "long", day: "numeric" }).format(new Date(eventOffer.refundFloorAt))}.`
                      : `${LIFETIME_REFUND_DAYS}-day money-back guarantee.`}
                  </p>
                  {claimError && (
                    <p className="text-sm text-red-200" role="alert" data-testid="text-syre-claim-error">
                      {claimError}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                <h1 className="text-4xl font-bold tracking-tight text-white md:text-5xl" data-testid="text-syre-heading">
                  This SYRE offer has closed.
                </h1>
                <p className="mx-auto mt-4 max-w-2xl text-lg text-blue-100" data-testid="text-syre-sub">
                  You can still try PadPulse free for {TRIAL_DAYS} days. No card needed.
                </p>
                {replayJump()}
                <div className="mt-8 flex justify-center">
                  <StartFreeButton ctaId="syre-closed-start-free" location="syre_closed" label="Start free" />
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      {offer.open && (
        <section className="border-b border-gray-100 px-4 py-10" data-testid="section-syre-proof">
          <div className="mx-auto grid max-w-3xl grid-cols-3 gap-4 text-center">
            {PROOF_TILES.map((t) => (
              <div key={t.label}>
                <div className="text-3xl font-bold text-brand md:text-4xl">{t.value}</div>
                <div className="mt-1 text-sm text-gray-600">{t.label}</div>
              </div>
            ))}
          </div>
          <p className="mx-auto mt-4 max-w-3xl text-center text-sm text-gray-600">{PROOF_LINE}</p>
        </section>
      )}

      {offer.open && (
        <section className="bg-gray-50 px-4 py-12 md:py-16" data-testid="section-syre-toolkit" aria-labelledby="syre-toolkit-heading">
          <div className="mx-auto max-w-6xl">
            <h2 id="syre-toolkit-heading" className="text-center text-3xl font-bold text-gray-900">
              Your all-in-one co-living toolkit
            </h2>
            <p className="mx-auto mb-8 mt-3 max-w-2xl text-center text-gray-700">
              Manage your houses on PadSplit, off PadSplit, or both. Explore the tabs and inspect actual app screenshots
              in every section. Hover or pause the automatic tour to inspect a workspace, and select any screenshot to enlarge it.
            </p>
            <FeatureExplorer
              previewMode="screenshots"
              ctaLabel="Claim the lifetime deal"
              onCtaClick={() => {
                track("cta_clicked", { cta_id: "syre_toolkit_claim", location: "syre_toolkit" });
                document.querySelector<HTMLElement>('[data-testid="button-syre-claim"]')?.focus();
                document.querySelector<HTMLElement>('[data-testid="button-syre-claim"]')?.scrollIntoView({ block: "center", behavior: "auto" });
              }}
            />
            <p className="mx-auto mt-4 max-w-3xl text-center text-xs leading-5 text-gray-600">
              Continuous Auto Draft requires a beta grant and explicit enabling; signup or payment alone does not grant access.
              The 60s tile describes the send/reconciliation cycle, not guaranteed inbound message arrival.
            </p>
          </div>
        </section>
      )}

      {hasReplay && <SyreWebinarSection url={offer.webinarReplayUrl!} title={offer.webinarTitle} />}

      {offer.open && (
        <main className="flex-grow px-4 py-12 md:py-16">
          <section className="mx-auto mb-12 max-w-3xl text-center" data-testid="section-syre-off-padsplit">
            <h2 className="text-2xl font-bold text-gray-900">Built for PadSplit. Works off PadSplit too.</h2>
            <p className="mt-3 text-gray-700">
              PadPulse connects to your PadSplit account. For rooms off PadSplit, your own member portal handles messages and booking requests.
            </p>
          </section>
          <div className="mx-auto grid max-w-5xl gap-10 md:grid-cols-2">
            <section data-testid="section-syre-included">
              <h2 className="text-2xl font-bold text-gray-900">What's included</h2>
              <ul className="mt-4 space-y-3 text-gray-800">
                {SYRE_INCLUDED_ROWS.map((row) => (
                  <li key={row.title} className="flex items-start gap-3">
                    <Check size={18} className="mt-1 shrink-0 text-emerald-600" />
                    <span>
                      <strong>{row.title}</strong> {row.body}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            <section data-testid="section-syre-how">
              <h2 className="text-2xl font-bold text-gray-900">How it works</h2>
              <ol className="mt-4 space-y-4">
                {[
                  "Create and verify your account.",
                  `Choose ${options.map((o) => o.properties).join(", ").replace(/, (\d+)$/, " or $1")} properties, then complete one-time checkout.`,
                  "PadPulse activates lifetime access after the server confirms payment.",
                  "Then connect PadSplit (or add rooms off PadSplit) and set up your properties.",
                ].map((step, i) => (
                  <li key={step} className="flex items-start gap-3 text-gray-800">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand text-sm font-bold text-white">{i + 1}</span>
                    <span className="pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </section>
          </div>

          <section id="offer-details" className="mx-auto mt-12 max-w-3xl scroll-mt-24 rounded-2xl border border-gray-200 bg-white p-5 text-left shadow-sm sm:p-7" aria-labelledby="syre-offer-details-heading" data-testid="section-syre-offer-details">
            <h2 id="syre-offer-details-heading" className="text-lg font-bold text-gray-900">Offer details</h2>
            <dl className="mt-4 divide-y divide-gray-200 text-sm">
              <div className="py-3">
                <dt className="font-semibold text-gray-900">Private Slack support</dt>
                <dd className="mt-1 text-gray-600">
                  Private Slack support and manual onboarding are included within one business day of payment.
                </dd>
              </div>
              {(eventOffer.standardLocks || hasLockPromotion) && (
                <div className="py-3">
                  <dt className="font-semibold text-gray-900">Reserved property locks</dt>
                  <dd className="mt-1 text-gray-600">
                    Each lock is valued at $100, with estimated Q2 2027 shipping. If a lock has not shipped by September 30, 2027, you receive $100 in PadPulse account credit for each undelivered lock. Credit has no cash value; a refund cancels the contribution and any related lock credit.
                  </dd>
                </div>
              )}
              {eventOffer.eventTerms && String(eventOffer.eventTerms ?? "").trim() !== String(SYRE_OCTOBER_EVENT.eventTerms).trim() && (
                <div className="py-3">
                  <dt className="font-semibold text-gray-900">Additional event terms</dt>
                  <dd className="mt-1 whitespace-pre-line text-gray-600">{eventOffer.eventTerms}</dd>
                </div>
              )}
            </dl>
          </section>
          <div className="mx-auto mt-5 max-w-3xl space-y-5 text-center">
            <p className="text-sm text-gray-600" data-testid="text-syre-fine-print">
              See the{" "}
              <a href={TERMS_PRICING_HREF} className="text-brand underline" data-testid="link-syre-terms">
                Terms
              </a>{" "}
              for complete lifetime plan details and refund terms.
            </p>
            <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
              <span className="text-gray-700">Not ready?</span>
              <StartFreeButton
                ctaId="syre-trial"
                location="syre"
                label={`Start a ${TRIAL_DAYS}-day free trial`}
                size="default"
                showArrow={false}
                className="border border-brand/30 bg-white text-brand shadow-none hover:bg-brand/5 hover:text-brand"
              />
              <span className="text-gray-700">No card needed.</span>
            </div>
            {close && closeLongDate && (
              <p className="text-sm font-semibold text-brand" data-testid="text-syre-trial-deadline">
                The lifetime offer closes {closeLongDate} at {close.time} ET. Starting a free trial does not extend this deadline.
              </p>
            )}
            <p className="text-gray-700" data-testid="text-syre-contact">
              Questions? Find Travis at the event or email{" "}
              <a href="mailto:travis@padpulse.io" className="text-brand underline">
                travis@padpulse.io
              </a>
              .
            </p>
          </div>
        </main>
      )}
      {!offer.open && <div className="flex-grow" />}
      <PublicFooter />
    </div>
  );
}
